import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { Ceiling } from "building-types/ceilings/ceiling";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import { InvalidRoomOutlineError } from "building-types/errors/buildingGenerationError";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { ARCHETYPE_INSTANCE_CHAIN_KEY, composeComponentInstanceChain } from "doc/entity/componentInstanceEntityResolve";
import { docEntityGetSortedChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { docEntityGetComponent } from "doc/entity/docGetEntityComponent";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import {
  buildEdgeModifiersByParentId,
  getEntityEdgeModifiersTable,
  polyLine2Modify,
  type EdgeModifiersByParentId,
} from "doc/utils/modifierUtils";
import { WindingDir } from "geom/core";
import { polyLine2Copy, polyLine2SetCCW, polyLine2WindingDir } from "geom/polyLine2";
import { getDatumAbstraction } from "../elements/datums/datumAbstractions";
import { staticRoomAttributes } from "../elements/roomAttributes";
import { checkRoomOutline, roomNew } from "../elements/rooms/roomTools";
import { applyTransformToRoom, getRoomComponentTransforms } from "../generationTools/components/componentRooms";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { evaluateComponentRoomParentContext } from "./evaluateComponentRoomParentContext";

interface SelectRoomsResult {
  readonly blocks: Building["blocks"];
  readonly rawBlocks: Building["doc"]["blocks"];
  readonly rooms: Building["doc"]["rooms"];
  readonly ceilings: Building["doc"]["ceilings"];
  readonly levels: Building["levels"];
  readonly rawLevels: Building["doc"]["levels"];
  readonly modifiers: Building["modifiers"];
  readonly modifiersByInstanceChain: Building["modifiersByInstanceChain"];
  readonly edgeModifiers: Building["doc"]["edgeModifiers"];
  readonly entityDisabledByGroup: Building["entityDisabledByGroup"];
  readonly blockOptionChoices: ReturnType<typeof resolveOptionsInFormula>;
  readonly roomOptionChoices: ReturnType<typeof resolveOptionsInFormula>;
  readonly ceilingOptionChoices: ReturnType<typeof resolveOptionsInFormula>;
  readonly levelOptionChoices: ReturnType<typeof resolveOptionsInFormula>;
  readonly definitions: OptionDefinitions;
  readonly entityRefs: Building["doc"]["entityRefs"];
  readonly idToParents: Building["idToParents"];
  readonly componentToTransforms: Building["componentToTransforms"];
  readonly enabledFeatures: Building["enabledFeatures"];
  readonly isolatedEntity: Building["isolatedEntity"];
  readonly datumAbstractions: Building["datumAbstractions"];
  readonly levelsAttributes: Building["levelsAttributes"];
  readonly componentMembers: Building["doc"]["componentMembers"];
}

function selectRooms({
  doc: {
    blocks: rawBlocks,
    rooms,
    ceilings,
    levels: rawLevels,
    edgeModifiers,
    optionSets,
    instanceOptionSets,
    entityRefs,
    componentMembers,
  },
  blocks,
  levels,
  modifiers,
  modifiersByInstanceChain,
  optionChoices,
  entityDisabledByGroup,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  isolatedEntity,
  datumAbstractions,
  levelsAttributes,
}: Building): SelectRoomsResult {
  const definitions = { optionSets, instanceOptionSets };
  const blockOptionChoices = resolveOptionsInFormula(rawBlocks, definitions, optionChoices);
  const roomOptionChoices = resolveOptionsInFormula({ rooms }, definitions, optionChoices);
  const ceilingOptionChoices = resolveOptionsInFormula({ ceilings }, definitions, optionChoices);
  const levelOptionChoices = resolveOptionsInFormula({ levels: rawLevels }, definitions, optionChoices);

  return {
    blocks,
    rawBlocks,
    rooms,
    ceilings,
    levels,
    rawLevels,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    entityDisabledByGroup,
    blockOptionChoices,
    roomOptionChoices,
    ceilingOptionChoices,
    levelOptionChoices,
    definitions,
    entityRefs,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    datumAbstractions,
    levelsAttributes,
    componentMembers,
  };
}

const compare = makeGenerationStageComparator<typeof selectRooms>({
  entityDisabledByGroup: "shallow",
  blocks: "deep",
  rawBlocks: "deep",
  rooms: "deep",
  ceilings: "deep",
  levels: "deep",
  rawLevels: "deep",
  blockOptionChoices: "deep",
  roomOptionChoices: "deep",
  ceilingOptionChoices: "deep",
  levelOptionChoices: "deep",
  definitions: "shallow",
  modifiers: "deep",
  edgeModifiers: "deep",
  idToParents: "shallow",
  componentToTransforms: "shallow",
  enabledFeatures: "shallow",
  isolatedEntity: "shallow",
  datumAbstractions: "shallow",
  levelsAttributes: "shallow",
  componentMembers: "shallow",
});

export function generateRooms(opts: ReturnType<typeof selectRooms>): { rooms: Table<Room>; blocks: RWTable<Block> } {
  const {
    blocks,
    rawBlocks,
    levels,
    rawLevels,
    modifiers,
    modifiersByInstanceChain,
    edgeModifiers,
    entityDisabledByGroup,
    rooms,
    ceilings,
    blockOptionChoices,
    roomOptionChoices,
    ceilingOptionChoices,
    levelOptionChoices,
    definitions,
    entityRefs,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    datumAbstractions,
    levelsAttributes,
    componentMembers,
  } = opts;

  // evaluate and remove disabled rooms
  const finalCeilings = evalFormulas(ceilings, (v) => ceilingOptionChoices[v]) as Table<Ceiling>;
  const generatedRooms: RWTable<Room> = {};

  const updatedBlocks: RWTable<Block> = {
    ...blocks,
  };

  const edgeModifiersByParentId = buildEdgeModifiersByParentId(edgeModifiers);

  // Cache evaluateComponentRoomParentContext results keyed by (blockId, instanceId).
  const parentContextCache = new Map<string, ReturnType<typeof evaluateComponentRoomParentContext>>();

  /**
   * Note that the iteration order of the blocks and rooms is important here.
   * Various systems depend on the order of the rooms table and will change in
   * subtle ways if the order changes.
   * */
  for (const blockId in rawBlocks) {
    for (const roomId in rooms) {
      const room = rooms[roomId];
      if (room.parentId !== blockId) {
        continue;
      }

      const component = docEntityGetComponent({ componentMembers }, { type: "ROOM", id: roomId });

      /**
       * if room sourced from doc ref and not part of a component, do not render in editor
       */
      if (entityRefs[roomId] && !component) {
        continue;
      }

      const staticRoom = evalFormulas(room, (v) => roomOptionChoices[v]);

      // if the room is part of a disabled group, the room is not included in the output
      if (entityDisabledByGroup[staticRoom.id]) {
        continue;
      }

      if (!staticRoom.disabled && blocks[blockId]) {
        const roomCeilings: ReadonlyArray<Ceiling> = docEntityGetSortedChildrenFromTable(finalCeilings, roomId).filter(
          (c) => entityDisabledByGroup[c.id] !== true && c.disabled !== true,
        );
        const roomCeiling = roomCeilings[0];
        const generatedRoom = generateRoom(
          staticRoom,
          roomCeiling,
          roomId,
          blocks,
          levels,
          levelsAttributes,
          edgeModifiersByParentId,
          modifiers,
          datumAbstractions,
        );

        // Check if this room should be generated based on the isolated entity.
        if (shouldGenerateEntity(roomId, idToParents, isolatedEntity)) {
          generatedRooms[roomId] = generatedRoom;
        }
      }

      if (enabledFeatures.modelComponents) {
        const componentTransforms = getRoomComponentTransforms(roomId, idToParents, componentToTransforms);
        for (const componentTransform of componentTransforms) {
          const { instanceId, componentInstanceChain } = componentTransform;

          const parentContextCacheKey = `${blockId}:${instanceId}`;
          if (!parentContextCache.has(parentContextCacheKey)) {
            parentContextCache.set(
              parentContextCacheKey,
              evaluateComponentRoomParentContext(
                room,
                componentTransform,
                rawBlocks,
                rawLevels,
                levels,
                definitions,
                blockOptionChoices,
                levelOptionChoices,
                entityDisabledByGroup,
              ),
            );
          }
          const evaluatedParentContext = parentContextCache.get(parentContextCacheKey);

          if (!evaluatedParentContext) {
            continue;
          }

          const { rawBlock, componentBlock, componentRoomLevel } = evaluatedParentContext;

          const staticRoom = evaluateEntityForComponentInstance(
            room,
            componentInstanceChain,
            definitions,
            roomOptionChoices,
          );
          if (staticRoom.disabled) {
            continue;
          }

          if (!shouldGenerateEntity(instanceId, idToParents, isolatedEntity)) {
            continue;
          }

          let roomCeiling: Ceiling | undefined;
          const roomCeilings = docEntityGetSortedChildrenFromTable(ceilings, roomId);
          for (const ceiling of roomCeilings) {
            const componentCeiling = evaluateEntityForComponentInstance(
              ceiling,
              componentInstanceChain,
              definitions,
              ceilingOptionChoices,
            );
            if (!componentCeiling.disabled && !entityDisabledByGroup[componentCeiling.id]) {
              roomCeiling = componentCeiling;
              break;
            }
          }

          // An uncovered chain falls back to the archetype world, and that in turn to `modifiers` for
          // stage sets that omit the modifiers stage entirely.
          const instanceModifiers =
            modifiersByInstanceChain[composeComponentInstanceChain(componentInstanceChain)] ??
            modifiersByInstanceChain[ARCHETYPE_INSTANCE_CHAIN_KEY] ??
            modifiers;

          const componentRoom = generateRoom(
            staticRoom,
            roomCeiling,
            roomId,
            blocks,
            levels,
            levelsAttributes,
            edgeModifiersByParentId,
            instanceModifiers,
            datumAbstractions,
            instanceId,
            rawBlock,
            componentRoomLevel,
          );
          const transformedRoom = applyTransformToRoom(
            componentRoom,
            componentTransform,
            levels,
            updatedBlocks,
            componentBlock,
          );
          generatedRooms[transformedRoom.id] = transformedRoom;
        }
      }
    }
  }
  return { rooms: generatedRooms, blocks: updatedBlocks };
}

function generateRoom(
  staticRoom: StaticRoom,
  roomCeiling: Ceiling | undefined,
  roomId: string,
  blocks: Table<Block>,
  levels: Table<Level>,
  levelsAttributes: Table<LevelAttributes>,
  edgeModifiersByParentId: EdgeModifiersByParentId,
  modifiers: Table<GeneratedModifier>,
  datumAbstractions: Table<DatumAbstraction>,
  instanceId?: string,
  sourceBlock?: Block,
  sourceLevel?: Level,
): Room {
  const {
    type,
    outline,
    edgeIds,
    name,
    nameInPlan,
    tagDetails,
    addCeilingHeightToRoomTag,
    ceilingHeightTagOverride,
    addFloorMaterialToRoomTag,
    floorMaterialTagOverride,
    wallMaterial,
    roomTagTemplateId,
    areaTagOverride,
    tagDimensionControl,
  } = staticRoom;

  const blockId = staticRoom.parentId;
  if (!blockId) {
    throw new Error(`Room ${roomId} has no blockId`);
  }

  const block = blocks[blockId] ?? sourceBlock;
  if (!block) {
    throw new Error(`Room ${roomId} has no source block`);
  }

  const level = sourceLevel ?? levels[block.levelId];
  if (!level) {
    throw new Error(`Room ${roomId} has no source level`);
  }
  const levelAttributes = levelsAttributes[level.id] ?? levelsAttributes[block.levelId];
  const { componentInstanceChain } = level;

  const ceilingMaterial = roomCeiling?.ceilingMaterial;
  const ceilingDatum = roomCeiling?.ceilingDatum;

  let ceilingHeightOffset = 0;
  if (ceilingDatum) {
    const ceilingDatumAbstraction = getDatumAbstraction({
      datumUsage: ceilingDatum,
      datumAbstractions,
      requestedUsageType: DatumVerticalUsageType.CEILING,
      componentInstanceChain,
    });
    const ceilingHeight = ceilingDatumAbstraction?.resolveVerticalDatumRelativeToLevel({
      level,
      levelAttributes,
      extraOffset: ceilingDatum.offset,
    });

    if (ceilingHeight !== undefined) {
      ceilingHeightOffset = ceilingHeight - level.topOfWall;
    }
  }

  const roomEdgeModifiers = getEntityEdgeModifiersTable(edgeModifiersByParentId, roomId);
  assertIsNotUndefined(edgeIds);
  const outlineAfterEdgeModify = polyLine2Modify(outline, roomEdgeModifiers, modifiers, edgeIds);
  const outlineFinal =
    polyLine2WindingDir(outlineAfterEdgeModify) === WindingDir.CCW
      ? outlineAfterEdgeModify
      : polyLine2SetCCW(polyLine2Copy(outlineAfterEdgeModify));

  const problem = checkRoomOutline(outlineFinal);
  if (problem) {
    throw new InvalidRoomOutlineError({ type: "ROOM", id: roomId }, problem);
  }

  const { floorIndex } = level;

  let finalAttributes = staticRoomAttributes(staticRoom);
  if (floorIndex < 0) {
    finalAttributes = {
      ...finalAttributes,
      roomClass: RoomClass.BASEMENT,
    };
  }

  const roomOptions = {
    id: roomId,
    type,
    outline: outlineFinal,
    sourceRoomId: roomId,
    instanceId,
    name: name ?? roomId,
    nameInPlan,
    tagDetails,
    addCeilingHeightToRoomTag,
    ceilingHeightTagOverride,
    addFloorMaterialToRoomTag,
    floorMaterialTagOverride,
    level,
    parentId: blockId,
    edgeIds,
    attributes: finalAttributes,
    ceilingHeightOffset,
    ceilingMaterial,
    wallMaterial,
    roomTagTemplateId,
    areaTagOverride,
    tagDimensionControl,
  };
  return roomNew(roomOptions);
}

export default memo(selectRooms, generateRooms, compare);
